import { useEffect, useMemo, useState } from 'react';
import type { Node } from '@xyflow/react';
import { NJButton, NJFormItem, NJIconButton } from '@engie-group/fluid-design-system-react';
import {
    NODE_TYPES,
    ProductionEditorCanvas,
    ProductionEditorOverlaySidebar,
    ProductionEditorProvider,
    useProductionEditor,
    type ComponentItem,
    type IBaseData,
} from '@shared/production-editor';
import { Size2MixInfoGrid } from '@shared/size2mix-shell';
import { useUpdateUnit } from './hooks';
import { buildSiteGraph } from './siteGraph';
import type { ProductionData } from './types';

const COMPONENTS: ComponentItem[] = [
    { id: 'c-boiler', label: 'Chaudière', entityName: 'Boiler', icon: 'flame' },
    { id: 'c-pump', label: 'Pompe à vitesse variable', entityName: 'VariableSpeedPump', icon: 'settings_input_component' },
    { id: 'c-pumpgroup', label: 'Groupe de pompes', entityName: 'PumpGroup', icon: 'view_module' },
    { id: 'c-fuel', label: 'Contrat de combustible', entityName: 'FuelContract', icon: 'local_gas_station' },
    { id: 'c-power', label: "Contrat d'électricité", entityName: 'PowerContract', icon: 'bolt' },
    { id: 'c-return', label: 'Retour', entityName: 'Return', icon: 'arrow_back' },
    { id: 'c-departure', label: 'Départ', entityName: 'Departure', icon: 'arrow_forward' },
];

export default function EditorTab({ data }: { data: ProductionData }) {
    const [siteName, setSiteName] = useState(data.sites[0]?.name ?? '');
    const [editing, setEditing] = useState<Node<IBaseData> | null>(null);
    const graph = useMemo(() => buildSiteGraph(data, siteName), [data, siteName]);

    return (
        <div style={{ display: 'flex', gap: 12, height: '100%', minHeight: 560 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                {/* key resets the canvas when switching site */}
                <ProductionEditorProvider key={siteName} initialNodes={graph.nodes} initialEdges={graph.edges}>
                    <Canvas
                        sites={data.sites.map((s) => ({ id: s.name, label: s.name }))}
                        siteName={siteName}
                        onSelectSite={(id) => {
                            setSiteName(id);
                            setEditing(null);
                        }}
                        onEditNode={setEditing}
                    />
                </ProductionEditorProvider>
            </div>
            {editing && <EditPanel node={editing} data={data} onClose={() => setEditing(null)} />}
        </div>
    );
}

function Canvas({
    sites,
    siteName,
    onSelectSite,
    onEditNode,
}: {
    sites: Array<{ id: string; label: string }>;
    siteName: string;
    onSelectSite: (id: string) => void;
    onEditNode: (n: Node<IBaseData>) => void;
}) {
    const { setNodes } = useProductionEditor();

    useEffect(() => {
        setNodes((prev) =>
            prev.map((n) => ({
                ...n,
                data: {
                    ...(n.data as IBaseData),
                    onEditClick: () => onEditNode(n as Node<IBaseData>),
                    onDeleteClick: () => setNodes((all) => all.filter((x) => x.id !== n.id)),
                },
            })),
        );
        // wire handlers once per mounted site graph
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ProductionEditorCanvas nodeTypes={NODE_TYPES} minHeight="100%">
            <ProductionEditorOverlaySidebar plants={sites} components={COMPONENTS} selectedPlantId={siteName}
                onSelectPlant={onSelectSite}
                onCreatePlant={() => undefined}
                plantsLabel="Centrales"
                componentsLabel="Composants"
                createPlantLabel="Créer une centrale"
            />
        </ProductionEditorCanvas>
    );
}

function EditPanel({ node, data, onClose }: { node: Node<IBaseData>; data: ProductionData; onClose: () => void }) {
    const label = String(node.data.label);
    const unit = data.units.find((u) => u.name === label);
    const updateUnit = useUpdateUnit();
    const [pMax, setPMax] = useState(unit ? String(unit.pMax) : '');

    return (
        <aside
            style={{
                width: 360,
                padding: 16,
                overflowY: 'auto',
                background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                    {String(node.data.entityLabel)} — {label}
                </h3>
                <NJIconButton icon="close" label="Fermer" variant="tertiary" scale="sm" onClick={onClose} />
            </div>
            {unit ? (
                <form
                    style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        updateUnit.mutate({ name: unit.name, patch: { pMax: Number(pMax) } });
                    }}
                >
                    <Size2MixInfoGrid
                        labelWidth={150}
                        rows={[
                            { label: 'Site', value: unit.site },
                            { label: 'Priorité', value: unit.priority ?? '-' },
                            { label: 'Contrat combustible', value: unit.fuelContract ?? '-' },
                            { label: 'Puissance min', value: `${unit.pMin} MW` },
                            { label: 'Rendement à Pmin', value: unit.efficiencyAtPMin },
                            { label: 'Rendement à Pmax', value: unit.efficiencyAtPMax },
                        ]}
                    />
                    <NJFormItem
                        id="ref-prod-pmax"
                        label="Puissance max [MW]"
                        labelKind="static"
                        type="number"
                        value={pMax}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPMax(e.target.value)}
                    />
                    <NJButton type="submit" label="Enregistrer" disabled={!pMax || Number(pMax) === unit.pMax || updateUnit.isPending} />
                    {updateUnit.isSuccess && <span style={{ color: 'var(--nj-semantic-color-text-status-success-default)' }}>Modifications enregistrées.</span>}
                </form>
            ) : (
                <p style={{ color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>Aucun paramètre éditable dans ce prototype pour cet élément.</p>
            )}
        </aside>
    );
}
